/**
 * LabelPickerModal - Attach existing labels to a contact, or create a new one
 * inline when the search has no exact match. Mirrors the visual language of
 * ScheduleMessageModal (overlay, container, header) so the chat menu feels
 * cohesive.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  X, Plus, Check, Loader2, Search, GitBranch, ChevronDown, ChevronRight,
} from 'lucide-react';
import authService from '../../services/global/authService';
import customFieldsService from '../../services/global/customFieldsService';
import managerDashboardService from '../../services/global/managerDashboardService';
import sseService, { SSE_EVENTS } from '../../services/global/sseService';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import OutcomeReasonModal from './OutcomeReasonModal';

const DEFAULT_COLORS = [
  '#25d366', '#00a884', '#10b981', '#22c55e',
  '#84cc16', '#eab308', '#f59e0b', '#f97316',
  '#ef4444', '#dc2626', '#ec4899', '#db2777',
  '#a855f7', '#8b5cf6', '#6366f1', '#3b82f6',
  '#0ea5e9', '#06b6d4', '#14b8a6', '#64748b',
];

function pickColor(existingCount) {
  return DEFAULT_COLORS[existingCount % DEFAULT_COLORS.length];
}

const LabelPickerModal = ({
  isOpen,
  onClose,
  contactPhone,
  contactName,
  colors,
  onChange,
  // 'modal' (default) ou 'drawer' (painel lateral direito — passado pelos
  // ChatWindows para alinhar com o ContactDetailsDrawer).
  displayMode = 'modal',
  // z-index do overlay. Quando aberto por cima do ContactDetailsDrawer (1200),
  // passe 1300 para ficar acima (igual ao ContactTagsManagerModal).
  zIndex = 1000,
}) => {
  const [allLabels, setAllLabels] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [attachedIds, setAttachedIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  // Funis recolhidos por padrão (mesmo padrão da aba Filtrar) — só o funil da
  // etapa ATUAL do contato abre sozinho; buscar expande os grupos com match.
  const [expandedFunnels, setExpandedFunnels] = useState(() => new Set());
  // Etapa de ganho/perda escolhida, aguardando o motivo. Este componente é o
  // ponto ÚNICO de mudança de etapa fora do Kanban (ContactDetailsDrawer +
  // os 4 ChatWindows), então o modal de motivo mora aqui e cobre todos os
  // canais de uma vez.
  const [pendingAssign, setPendingAssign] = useState(null);
  // Multi-funil (flag multi_funnel, 2026-09-16): a etapa nova substitui só a
  // etapa do MESMO funil; as dos outros funis ficam. Sem a flag, uma etiqueta
  // por contato, como sempre.
  const { flags: featureFlags } = useFeatureFlags();
  const multiFunnel = Boolean(featureFlags?.multi_funnel);
  // Config de CRM (cacheada no service): campo "Valor da venda" no ganho para
  // contas com funil comercial. Buscada na abertura do picker.
  const [crmConfig, setCrmConfig] = useState(null);
  useEffect(() => {
    if (!isOpen) return undefined;
    let alive = true;
    managerDashboardService.getCrmConfig().then((cfg) => {
      if (alive) setCrmConfig(cfg);
    });
    return () => { alive = false; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !contactPhone) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setSearch('');

    Promise.all([
      authService.getLabels(),
      authService.getContactLabels(contactPhone),
      authService.listFunnels().catch(() => []),
    ])
      .then(([labelsRes, attachedRes, funnelsRes]) => {
        if (cancelled) return;
        const labels = labelsRes?.data || [];
        const attached = attachedRes?.data || [];
        setAllLabels(labels);
        setFunnels(funnelsRes || []);
        setAttachedIds(new Set(attached.map((l) => l.id)));
        // Abre só o(s) funil(is) onde o contato está (os demais ficam recolhidos).
        const currentPids = attached.map((l) => l.pipeline_id).filter(Boolean);
        setExpandedFunnels(new Set(currentPids));
      })
      .catch((err) => {
        if (!cancelled) {
          setError('Não foi possível carregar as etapas.');
          console.error(err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, contactPhone]);

  const toggleFunnelExpand = (fid) => {
    setExpandedFunnels((prev) => {
      const n = new Set(prev);
      if (n.has(fid)) n.delete(fid); else n.add(fid);
      return n;
    });
  };

  const normalizedSearch = search.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!normalizedSearch) return allLabels;
    return allLabels.filter((l) => (l.name || '').toLowerCase().includes(normalizedSearch));
  }, [allLabels, normalizedSearch]);

  const exactMatch = useMemo(
    () => allLabels.find((l) => (l.name || '').toLowerCase() === normalizedSearch),
    [allLabels, normalizedSearch]
  );
  const canCreateNew = normalizedSearch && !exactMatch && allLabels.length < 20;

  // Agrupa as etiquetas (filtradas) por FUNIL, para o operador ver de qual
  // funil é cada etiqueta ao mudar o contato. Funis ordenados; dentro, as
  // colunas por `order`.
  const groupedByFunnel = useMemo(() => {
    const byFunnel = new Map();
    for (const l of filtered) {
      const pid = l.pipeline_id || '__none__';
      if (!byFunnel.has(pid)) byFunnel.set(pid, []);
      byFunnel.get(pid).push(l);
    }
    const ordered = [];
    const sortedFunnels = [...funnels].sort((a, b) => (a.order || 0) - (b.order || 0));
    for (const f of sortedFunnels) {
      const list = (byFunnel.get(f.id) || []).sort((a, b) => (a.order || 0) - (b.order || 0));
      if (list.length) ordered.push({ funnel: f, labels: list });
    }
    // Etiquetas sem funil conhecido (legado) caem num grupo solto.
    const orphan = byFunnel.get('__none__') || [];
    if (orphan.length) ordered.push({ funnel: { id: '__none__', name: 'Sem funil' }, labels: orphan });
    return ordered;
  }, [filtered, funnels]);

  const notify = (nextAttachedIds) => {
    if (typeof onChange === 'function') {
      onChange(Array.from(nextAttachedIds));
    }
    // Eco LOCAL do sucesso confirmado (mesmo shape do SSE do backend):
    // ChatLists e o hook do chip atualizam na hora, sem esperar o
    // round-trip do servidor — e o SSE real que chegar depois vira no-op
    // idempotente no patch dirigido. Cobre tambem SSE reconectando.
    try {
      sseService.emit(SSE_EVENTS.CONTACT_LABELS_CHANGED, {
        phone: String(contactPhone || '').replace(/\D/g, ''),
        label_ids: Array.from(nextAttachedIds),
      });
    } catch {}
  };

  const handleAssign = async (label) => {
    if (busyId) return;
    // Etapa que FECHA o funil: pergunta o motivo antes de gravar, igual ao
    // arrasto no Kanban. Pular grava sem motivo — nunca trava a mudança.
    if (label?.outcome === 'won' || label?.outcome === 'lost') {
      setPendingAssign(label);
      return;
    }
    await doAssign(label);
  };

  // Estado local depois de entrar em `label`: com multi-funil sai só a etapa
  // do mesmo funil; sem a flag, sai tudo (1 etiqueta por contato). O backend
  // aplica a mesma regra; aqui é só a UI fluida, sem esperar o refetch.
  const nextAttached = (label) => {
    if (!multiFunnel) return new Set([label.id]);
    const byId = new Map(allLabels.map((l) => [l.id, l]));
    const next = new Set();
    for (const id of attachedIds) {
      const cur = byId.get(id);
      if (!cur) continue;
      if ((cur.pipeline_id || '') === (label.pipeline_id || '')) continue;
      next.add(id);
    }
    next.add(label.id);
    return next;
  };

  const doAssign = async (label, outcomeReason, saleValue, outcomeAt) => {
    if (!label || busyId) return;
    setBusyId(label.id);
    setError(null);
    try {
      // Regra: 1 etiqueta por contato (ou 1 por funil com multi-funil). O
      // backend ja substitui a anterior; aqui atualizamos o estado local
      // para refletir a UI fluida (sem precisar remover antes).
      await authService.assignLabelToContact(contactPhone, label.id, undefined, outcomeReason, outcomeAt);
      // Valor da venda do modal de ganho: best-effort depois da etiqueta
      // (falha não desfaz a mudança — dá para preencher no drawer).
      if (saleValue != null && crmConfig?.value_field) {
        customFieldsService
          .setContactFields(contactPhone, { [crmConfig.value_field]: saleValue })
          .catch((e) => console.warn('[LabelPicker] valor da venda não salvo:', e));
      }
      const next = nextAttached(label);
      setAttachedIds(next);
      notify(next);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.code === 'required_fields') {
        const faltam = [...(d.missing || []).map((m) => m.name), ...(d.name_missing ? ['nome do lead'] : [])];
        setError(`Preencha na ficha do contato antes de mover: ${faltam.join(', ')}.`);
      } else {
        setError('Erro ao mover para a etapa. Tente novamente.');
        console.error(err);
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (labelId) => {
    if (busyId) return;
    setBusyId(labelId);
    setError(null);
    try {
      await authService.removeLabelFromContact(contactPhone, labelId);
      const next = new Set(attachedIds);
      next.delete(labelId);
      setAttachedIds(next);
      notify(next);
    } catch (err) {
      setError('Erro ao tirar da etapa. Tente novamente.');
      console.error(err);
    } finally {
      setBusyId(null);
    }
  };

  const handleCreateAndAssign = async () => {
    if (!canCreateNew || creating) return;
    const name = search.trim();
    setCreating(true);
    setError(null);
    try {
      const created = await authService.createLabel(name, pickColor(allLabels.length));
      const newLabel = created?.data || created;
      const updatedLabels = [...allLabels, newLabel];
      setAllLabels(updatedLabels);
      setSearch('');
      await authService.assignLabelToContact(contactPhone, newLabel.id);
      // Regra: 1 etiqueta por contato (1 por funil com multi-funil).
      const next = nextAttached(newLabel);
      setAttachedIds(next);
      notify(next);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Erro ao criar etapa.');
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  const attachedLabels = allLabels.filter((l) => attachedIds.has(l.id));
  const isDrawer = displayMode === 'drawer';

  return (
    <>
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex,
      display: 'flex',
      alignItems: isDrawer ? 'stretch' : 'center',
      justifyContent: isDrawer ? 'flex-end' : 'center',
      padding: isDrawer ? 0 : '20px',
    }}
    onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: colors.bgPrimary,
          borderRadius: isDrawer ? 0 : '16px',
          width: isDrawer ? 420 : '100%',
          maxWidth: isDrawer ? '100%' : '440px',
          overflow: 'hidden',
          boxShadow: isDrawer
            ? '-8px 0 32px rgba(0,0,0,0.25)'
            : '0 20px 60px rgba(0,0,0,0.3)',
          height: isDrawer ? '100%' : 'auto',
          maxHeight: isDrawer ? '100%' : '85vh',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
          flexShrink: 0,
        }}>
          <div>
            <h3 style={{
              margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary,
              display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <GitBranch size={18} color={colors.accentPrimary} /> Funil / etapa
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: colors.textSecondary }}>
              Para: {contactName || contactPhone}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary,
            padding: '8px', borderRadius: '8px',
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{
          padding: '16px 20px', display: 'flex', flexDirection: 'column',
          gap: '14px', overflowY: 'auto', flex: 1,
        }}>
          {/* Attached chips */}
          {attachedLabels.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span style={{ fontSize: '12px', fontWeight: 500, color: colors.textSecondary }}>
                {multiFunnel && attachedLabels.length > 1 ? 'Etapas atuais (uma por funil)' : 'Etapa atual'}
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {attachedLabels.map((label) => (
                  <span key={label.id} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    backgroundColor: `${label.color}22`, color: label.color,
                    fontSize: '12px', fontWeight: 600, padding: '4px 10px',
                    borderRadius: '999px', lineHeight: '16px',
                  }}>
                    {(() => {
                      const fn = funnels.find((f) => f.id === label.pipeline_id)?.name;
                      return fn ? `${fn} · ${label.name}` : label.name;
                    })()}
                    <button
                      onClick={() => handleRemove(label.id)}
                      disabled={busyId === label.id}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        background: 'transparent', border: 'none', color: 'inherit',
                        cursor: 'pointer', padding: 0, opacity: busyId === label.id ? 0.5 : 1,
                      }}
                      title={multiFunnel ? 'Sair deste funil' : 'Remover do funil'}
                    >
                      {busyId === label.id ? (
                        <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                      ) : (
                        <X size={12} />
                      )}
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Search input */}
          <div style={{
            display: 'flex', alignItems: 'center',
            backgroundColor: colors.bgSecondary, borderRadius: '8px',
            padding: '0 12px', border: `1px solid ${colors.borderColor}`,
          }}>
            <Search size={16} color={colors.textTertiary} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar ou criar nova etapa..."
              maxLength={50}
              style={{
                flex: 1, padding: '10px 12px', border: 'none', background: 'transparent',
                fontSize: '14px', color: colors.textPrimary, outline: 'none',
              }}
            />
          </div>

          {/* Create-new option */}
          {canCreateNew && (
            <button
              onClick={handleCreateAndAssign}
              disabled={creating}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                width: '100%', padding: '10px 12px',
                background: 'transparent', cursor: creating ? 'wait' : 'pointer',
                border: `1px dashed ${colors.accentPrimary}`, borderRadius: '8px',
                color: colors.accentPrimary, fontSize: '13px', fontWeight: 600,
                opacity: creating ? 0.6 : 1,
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${colors.accentPrimary}10`; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              {creating ? (
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Plus size={14} />
              )}
              Criar “{search.trim()}”
            </button>
          )}

          {allLabels.length >= 20 && !exactMatch && normalizedSearch && (
            <span style={{ fontSize: '12px', color: colors.textTertiary }}>
              Limite de 20 etapas atingido.
            </span>
          )}

          {/* List — funis como cards recolhíveis (mesmo padrão da aba Filtrar).
              Ao buscar, os grupos com match abrem sozinhos. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {loading ? (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '24px 0', color: colors.textTertiary,
              }}>
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            ) : groupedByFunnel.length === 0 ? (
              <span style={{ fontSize: '13px', color: colors.textTertiary, padding: '8px 4px' }}>
                {normalizedSearch ? 'Nenhuma etapa encontrada.' : 'Você ainda não criou nenhuma etapa.'}
              </span>
            ) : (
              groupedByFunnel.map(({ funnel, labels }) => {
                const hasCurrent = labels.some((l) => attachedIds.has(l.id));
                const open = !!normalizedSearch || expandedFunnels.has(funnel.id);
                return (
                  <div key={funnel.id} style={{
                    border: `1px solid ${hasCurrent ? colors.accentPrimary : colors.borderColor}`,
                    borderRadius: 10, overflow: 'hidden',
                  }}>
                    <div
                      onClick={() => toggleFunnelExpand(funnel.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '8px 10px', backgroundColor: colors.bgSecondary,
                        cursor: 'pointer',
                      }}
                    >
                      {open ? <ChevronDown size={16} color={colors.textTertiary} /> : <ChevronRight size={16} color={colors.textTertiary} />}
                      <span style={{
                        flex: 1, minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: 6,
                        fontSize: 13, fontWeight: 700, color: colors.textPrimary,
                      }}>
                        <GitBranch size={13} color={colors.accentPrimary} style={{ flexShrink: 0 }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{funnel.name}</span>
                        <span style={{
                          fontSize: 10, fontWeight: 700,
                          color: hasCurrent ? '#fff' : colors.textTertiary,
                          backgroundColor: hasCurrent ? colors.accentPrimary : colors.bgTertiary,
                          borderRadius: 10, minWidth: 18, height: 18, padding: '0 6px',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                          {labels.length}
                        </span>
                      </span>
                      {hasCurrent && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: colors.accentPrimary, whiteSpace: 'nowrap', flexShrink: 0 }}>
                          Etapa atual aqui
                        </span>
                      )}
                    </div>
                    {open && (
                      <div style={{ padding: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {labels.map((label) => {
                          const attached = attachedIds.has(label.id);
                          const isBusy = busyId === label.id;
                          return (
                            <button
                              key={label.id}
                              onClick={() => (attached ? handleRemove(label.id) : handleAssign(label))}
                              disabled={isBusy}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                width: '100%', padding: '9px 10px',
                                background: attached ? `${label.color}12` : 'transparent',
                                border: 'none', borderRadius: '8px',
                                cursor: isBusy ? 'wait' : 'pointer', textAlign: 'left',
                                transition: 'background-color 0.15s',
                              }}
                              onMouseEnter={(e) => { if (!attached) e.currentTarget.style.backgroundColor = colors.bgSecondary; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = attached ? `${label.color}12` : 'transparent'; }}
                            >
                              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ width: 14, height: 14, borderRadius: 4, backgroundColor: label.color, flexShrink: 0 }} />
                                <span style={{
                                  fontSize: '14px',
                                  color: attached ? label.color : colors.textPrimary,
                                  fontWeight: attached ? 600 : 400,
                                }}>
                                  {label.name}
                                </span>
                              </span>
                              {isBusy ? (
                                <Loader2 size={16} color={colors.textSecondary} style={{ animation: 'spin 1s linear infinite' }} />
                              ) : attached ? (
                                <Check size={16} color={label.color} />
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {error && (
            <span style={{ fontSize: '12px', color: '#ef4444' }}>{error}</span>
          )}
        </div>

        {/* Footer — as mudanças já são aplicadas ao clicar na etiqueta (não há
            "salvar"). O botão é só um "Fechar" neutro + aviso, para o usuário não
            achar que precisa confirmar (era um CTA "Concluído" que confundia). */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 20px', borderTop: `1px solid ${colors.borderColor}`,
          flexShrink: 0, gap: 12,
        }}>
          <span style={{ fontSize: '12px', color: colors.textTertiary }}>
            {multiFunnel
              ? 'As alterações são aplicadas automaticamente. Uma etapa por funil: escolher uma etapa de outro funil coloca o contato nele também.'
              : 'As alterações são aplicadas automaticamente.'}
          </span>
          <button
            onClick={onClose}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0,
              padding: '8px 18px', backgroundColor: 'transparent', color: colors.textSecondary,
              border: `1px solid ${colors.borderColor}`, borderRadius: '8px',
              fontSize: '13px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>

    {/* Motivo do fechamento. Fica FORA do overlay do picker (que fecha no
        clique) — aninhado, o clique vazaria e fecharia os dois. */}
    <OutcomeReasonModal
      isOpen={!!pendingAssign}
      colors={colors}
      outcome={pendingAssign?.outcome}
      labelName={pendingAssign?.name}
      contactName={contactName}
      options={pendingAssign?.outcome_reason_options || []}
      askValue={!!crmConfig?.value_field}
      askDate={!!crmConfig?.outcome_date}
      onConfirm={(reason, extra) => {
        const label = pendingAssign;
        setPendingAssign(null);
        doAssign(label, reason, extra?.saleValue, extra?.outcomeAt);
      }}
      onSkip={(extra) => {
        const label = pendingAssign;
        setPendingAssign(null);
        doAssign(label, undefined, extra?.saleValue, extra?.outcomeAt);
      }}
    />
    </>
  );
};

export default LabelPickerModal;
